import { useEffect, useRef } from 'react'
import { supabase } from '@/shared/lib/supabase/client'

export interface RealtimeWatch {
  table: string
  // PostgREST style filter, for example `connector_id=eq.<uuid>`. Without one the
  // subscription receives every row change the caller is allowed to see.
  filter?: string
  event?: 'INSERT' | 'UPDATE' | 'DELETE' | '*'
}

// Coalesces a burst of changes into one refetch. A single status change can produce
// several events, and refetching per event would hammer the API for no benefit.
const COALESCE_MS = 250

// Subscribes to Postgres changes and calls onChange when anything relevant moves.
// Deliberately refetches rather than patching local state from the payload: the pages
// select specific columns and derive counts from them, so a partial payload would
// drift from what the query returns.
export function useRealtime(
  channelName: string,
  watches: RealtimeWatch[],
  onChange: () => void,
  enabled = true
) {
  // Held in a ref so a new inline callback on each render cannot tear the
  // subscription down and rebuild it.
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  // Same reason: an inline array literal is a new identity every render, so the
  // effect keys off the serialised shape instead.
  const key = JSON.stringify(watches)

  useEffect(() => {
    if (!enabled || watches.length === 0) return

    let timer: ReturnType<typeof setTimeout> | undefined

    const schedule = () => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => onChangeRef.current(), COALESCE_MS)
    }

    const channel = supabase.channel(channelName)

    for (const watch of JSON.parse(key) as RealtimeWatch[]) {
      channel.on(
        'postgres_changes',
        {
          event: watch.event ?? '*',
          schema: 'public',
          table: watch.table,
          ...(watch.filter ? { filter: watch.filter } : {}),
        } as never,
        schedule
      )
    }

    channel.subscribe()

    return () => {
      if (timer) clearTimeout(timer)
      supabase.removeChannel(channel)
    }
  }, [channelName, key, enabled])
}
